import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { SectionHeading } from "@/components/SectionHeading";
import { images, promises, services, testimonials, values } from "@/data/content";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-2">
    <motion.span
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <>
      <SEO
        title="Bello Joy Consulting | Protection That Feels Like Family"
        description="Faith-centered insurance and financial consulting led by Alicia Joy, serving families and businesses across the United States."
      />

      <section ref={heroRef} data-testid="home-hero" className="hero-grain relative min-h-screen overflow-hidden px-6 pt-28">
        <div className="absolute left-[-10rem] top-24 h-80 w-80 rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-secondary/20 blur-3xl" />
        <div className="relative z-10 mx-auto grid max-w-7xl gap-12 lg:min-h-[calc(100vh-7rem)] lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="py-14">
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65 }}
              data-testid="hero-kicker"
              className="mb-6 inline-flex items-center gap-3 rounded-full border border-secondary/40 bg-card/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-primary shadow-sm"
            >
              <Sparkles size={14} className="text-secondary" /> Faith • Family • Future
            </motion.p>
            <h1 data-testid="hero-headline" className="text-balance text-5xl leading-[1.02] tracking-tight text-primary sm:text-6xl lg:text-7xl xl:text-8xl">
              <MaskedLine delay={0.1}>Protection that</MaskedLine>
              <MaskedLine delay={0.22}>
                feels like <span className="italic text-secondary">family.</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.42 }}
              data-testid="hero-welcome-message"
              className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
            >
              Bello Joy Consulting is a trusted insurance and consulting relationship led by Alicia Joy—serving clients with faith, integrity, excellence, and 25 years of industry wisdom.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.55 }}
              className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap"
            >
              <Button asChild size="lg" className="rounded-full bg-primary px-7 text-primary-foreground shadow-lift transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-primary/90">
                <Link to="/contact" data-testid="hero-protect-family-button">Protect Your Family Today <ArrowRight size={17} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-primary/25 bg-transparent px-7 text-primary transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-secondary/20">
                <Link to="/contact" data-testid="hero-schedule-consultation-button">Schedule a Consultation</Link>
              </Button>
              <Link to="/contact?service=Financial%20Consulting" data-testid="hero-financial-plan-link" className="editorial-link inline-flex items-center gap-2 self-center text-sm font-semibold text-primary">
                Start Your Financial Protection Plan <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>

          <motion.div style={{ y: imageY }} className="relative min-h-[520px] pb-20 lg:min-h-[680px] lg:pb-0" data-testid="hero-visual">
            <motion.div
              initial={{ clipPath: "inset(0% 44% 0% 44% round 2rem)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0% round 2rem)" }}
              transition={{ duration: 1.15, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ rotateX: 1.4, rotateY: -1.8 }}
              className="image-frame absolute inset-0 overflow-hidden rounded-[2rem] shadow-soft"
            >
              <motion.img
                initial={{ scale: 1.18 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                src={images.hero}
                alt="A warm family sharing a meaningful moment together"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/35 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              style={{ y: cardY }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              data-testid="hero-experience-card"
              className="absolute bottom-8 left-6 max-w-xs rounded-3xl border border-white/50 bg-background/85 p-6 shadow-lift backdrop-blur-xl md:left-10"
            >
              <p className="font-serif text-5xl italic text-secondary">25</p>
              <p className="mt-1 text-sm font-semibold uppercase tracking-[0.2em] text-primary">Years of trusted guidance</p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">Helping people make confident decisions with clarity and care.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee />

      <section data-testid="welcome-section" className="px-6 py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <Reveal className="relative" data-testid="welcome-image-wrap">
            <div className="image-frame overflow-hidden rounded-[2rem] shadow-soft">
              <img src={images.family} alt="A parent and child sharing a joyful moment" className="aspect-[4/5] w-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-8 -right-4 rounded-3xl bg-primary p-6 text-primary-foreground shadow-lift md:-right-8">
              <p className="font-serif text-2xl italic text-secondary">“Serve first.”</p>
              <p className="mt-2 text-xs uppercase tracking-[0.22em] text-primary-foreground/70">The Bello Joy way</p>
            </div>
          </Reveal>
          <div>
            <SectionHeading
              testId="why-choose-heading"
              kicker="Why clients choose us"
              title="Not just insurance. A relationship built on trust."
              description="Alicia Joy believes protection begins with listening. She takes time to understand your story, your goals, your concerns, and your dreams before recommending a solution."
            />
            <div className="grid gap-4" data-testid="why-choose-list">
              {[
                "Personal guidance from a 25-year insurance professional",
                "Education-first conversations without pressure",
                "Strategies shaped around your family, business, and legacy",
              ].map((item, index) => (
                <Reveal key={item} delay={index * 0.08} className="flex items-start gap-4 rounded-2xl border border-border bg-card/70 p-5" data-testid={`why-choose-item-${index + 1}`}>
                  <CheckCircle2 className="mt-1 shrink-0 text-secondary" size={22} />
                  <p className="text-base leading-relaxed text-primary/85">{item}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="meet-alicia-preview" className="bg-primary px-6 py-24 text-primary-foreground md:py-32 dark-grain relative overflow-hidden">
        <div className="relative z-10 mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <Reveal className="order-2 lg:order-1">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-secondary">Meet Alicia Joy</p>
            <h2 className="text-balance text-4xl leading-tight tracking-tight md:text-6xl">The heart behind Bello Joy Consulting.</h2>
            <p className="mt-6 text-base leading-relaxed text-primary-foreground/72 md:text-lg">
              Alicia is a compassionate advisor, trusted advocate, and experienced insurance professional known for integrity, reliability, and a genuine desire to serve others. Her mission is not simply selling products—it is building lifelong relationships and helping people protect the blessings they have worked hard to create.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="alicia-values-preview">
              {values.map((value, index) => (
                <div key={value.title} data-testid={`alicia-value-${index + 1}`} className="rounded-2xl border border-primary-foreground/15 bg-primary-foreground/5 p-4">
                  <p className="font-serif text-lg italic text-secondary">{value.title}</p>
                </div>
              ))}
            </div>
            <Button asChild className="mt-9 rounded-full bg-secondary px-7 text-primary transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-secondary/90">
              <Link to="/about" data-testid="meet-alicia-button">Meet Alicia <ArrowRight size={17} /></Link>
            </Button>
          </Reveal>
          <Reveal delay={0.12} className="order-1 lg:order-2" data-testid="meet-alicia-image-wrap">
            <div className="image-frame relative overflow-hidden rounded-[2rem] shadow-soft">
              <img src={images.alicia} alt="A warm professional advisor in an elegant office" className="aspect-[4/5] w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="services-overview" className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            testId="services-overview-heading"
            kicker="Services"
            title="Guidance for every season of life."
            description="Personalized insurance and financial consulting designed to protect your family, your future, your business, and the legacy you are building."
          />
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Reveal key={service.slug} delay={index * 0.05} className={index === 0 ? "md:col-span-2" : ""} data-testid={`home-service-card-${service.slug}`}>
                  <Link to={`/services#${service.slug}`} data-testid={`home-service-link-${service.slug}`} className="group flex h-full flex-col rounded-3xl border border-border bg-card/70 p-7 transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-card hover:shadow-lift">
                    <div className="mb-8 flex items-start justify-between">
                      <span className="font-serif text-4xl italic text-secondary">{service.number}</span>
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-primary transition-colors duration-300 group-hover:bg-secondary/30">
                        <Icon size={22} />
                      </span>
                    </div>
                    <h3 className="text-2xl tracking-tight text-primary">{service.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{service.summary}</p>
                    <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                      Explore guidance <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="customer-promise-section" className="border-y border-border bg-muted/50 px-6 py-24 md:py-32">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            testId="customer-promise-heading"
            kicker="Alicia’s customer promise"
            title="Five commitments. One standard of care."
            description="Every recommendation begins with respect—for your story, your stewardship, and the people depending on you."
          />
          <div className="grid gap-x-12 gap-y-10 lg:grid-cols-2" data-testid="promise-chapters">
            {promises.map((promise, index) => (
              <Reveal key={promise.number} delay={index * 0.06} className="grid gap-5 border-t border-primary/15 pt-7 sm:grid-cols-[6rem_1fr]" data-testid={`promise-chapter-${promise.number}`}>
                <span className="font-serif text-6xl italic leading-none text-secondary/80">{promise.number}</span>
                <div>
                  <h3 className="text-2xl tracking-tight text-primary">{promise.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">{promise.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="testimonials-section" className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            testId="testimonials-heading"
            kicker="Client words"
            title="Stories of trust, care, and long-term relationships."
            description="Sample testimonials are shown for launch review and should be replaced with Alicia’s client-approved stories."
            align="center"
          />
          <div className="grid gap-6 lg:grid-cols-3" data-testid="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <Reveal key={testimonial.name} delay={index * 0.08} className="rounded-[2rem] border border-border bg-card p-8 shadow-soft" data-testid={`testimonial-card-${index + 1}`}>
                <blockquote className="text-xl leading-relaxed text-primary">“{testimonial.quote}”</blockquote>
                <div className="mt-8 border-t border-border pt-5">
                  <p className="font-semibold text-primary">{testimonial.name}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{testimonial.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-final-cta" className="px-6 pb-24 md:pb-32">
        <Reveal className="dark-grain relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-primary px-8 py-16 text-primary-foreground shadow-soft md:px-16 md:py-20">
          <div className="relative z-10 max-w-3xl">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-secondary">Begin with a conversation</p>
            <h2 className="text-balance text-4xl leading-tight md:text-6xl">Your family’s future deserves thoughtful care.</h2>
            <p className="mt-5 text-base leading-relaxed text-primary-foreground/72 md:text-lg">
              Reach out to Alicia for warm, honest guidance—whether you are protecting a growing family, planning retirement, or preparing a legacy.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-full bg-secondary px-8 text-primary transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-secondary/90">
                <Link to="/contact" data-testid="final-cta-schedule-button">Schedule a Consultation <ArrowRight size={17} /></Link>
              </Button>
              <a href="/downloads/bello-joy-family-protection-checklist.pdf" download data-testid="home-download-checklist-link" className="editorial-link inline-flex items-center gap-2 text-sm font-semibold text-secondary">
                Download the Family Protection Checklist <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default Home;
